import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ProfileSummary } from '@bidmanager/shared';
import { errorMessage, request } from '../api';

export function Profiles(): JSX.Element {
  const navigate = useNavigate();
  const { data, error, isLoading } = useQuery({
    queryKey: ['profiles'],
    queryFn: () => request<ProfileSummary[]>('GET', '/profiles'),
  });

  if (isLoading) return <p className="muted">Loading profiles…</p>;
  if (error) return <p className="error">{errorMessage(error)}</p>;

  const incomplete = data?.filter((profile) => !profile.isComplete) ?? [];

  return (
    <>
      <header className="page-head">
        <div className="row">
          <div>
            <h1>Profiles</h1>
            <p className="muted small" style={{ margin: 0 }}>
              Each profile is one person's professional record. Resumes are generated from it.
            </p>
          </div>
          <button className="primary" onClick={() => navigate('/profiles/new')}>
            New profile
          </button>
        </div>
      </header>

      {/*
        Surfaced at the top, not just as a per-row badge. A bidder blocked by an
        incomplete profile cannot fix it themselves and has no way to reach the
        client except out of band — so the client needs to see it on arrival.
      */}
      {incomplete.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--warn)', marginBottom: 16 }}>
          <strong className="warn">
            {incomplete.length} {incomplete.length === 1 ? 'profile needs' : 'profiles need'} more
            information
          </strong>
          <p className="muted small" style={{ margin: '5px 0 0' }}>
            Bidders cannot generate documents for {incomplete.map((p) => p.fullName).join(', ')}{' '}
            until the missing sections are filled in.
          </p>
        </div>
      )}

      {data?.length === 0 && (
        <div className="card muted">No profiles yet. Create one to start assigning bidders.</div>
      )}

      <div className="list">
        {data?.map((profile) => (
          <div key={profile.id} className="card">
            <div className="row">
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{profile.fullName}</div>
                <div className="muted small">
                  {profile.location} · {profile.assignedBidderCount} bidder
                  {profile.assignedBidderCount === 1 ? '' : 's'} · {profile.openTargetCount} open
                  target{profile.openTargetCount === 1 ? '' : 's'}
                </div>
              </div>
              {profile.isComplete ? (
                <span className="badge ok">Ready</span>
              ) : (
                <span className="badge warn">Needs {profile.missing.join(' & ')}</span>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Link to={`/profiles/${profile.id}`}>
                <button className="sm">Edit profile</button>
              </Link>
              <Link to={`/profiles/${profile.id}/targets`}>
                <button className="sm">Bid targets</button>
              </Link>
              <Link to={`/profiles/${profile.id}/bidders`}>
                <button className="sm">Bidders</button>
              </Link>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
